import React, { useState, useRef } from 'react'
import { Play, Pause, Download, Share2, Video } from 'lucide-react'
import { useBlobUrlFromApi } from '../hooks/useBlobUrl'

/**
 * VideoCard component for centralized video blob URL management
 * Similar to ImageCard but optimized for video content
 */
const VideoCard = ({ 
  video, 
  pageType = 'satsueiDataToroku',
  imageService,
  serviceParams = [],
  className = '',
  showControls = true,
  autoPlay = false,
  onVideoClick = null,
  onDownload = null,
  onShare = null
}) => {
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  
  // Create stable dependencies to prevent unnecessary re-fetches
  const videoKey = `${video.objectId}-${video.objectMovieId}-${video.filenameExtension}-${video.isUploaded || false}`
  
  // Early check to prevent hook from running if we know the video shouldn't be loaded
  const shouldSkipVideoLoad = video.isUploaded === 0 || video.isUploaded === "0" || video.isUploaded === false

  // Get MIME type based on video extension
  const getVideoMimeType = (extension) => {
    if (!extension) return 'video/mp4' // default
    
    const ext = extension.toLowerCase()
    const mimeTypes = {
      'mp4': 'video/mp4',
      'avi': 'video/x-msvideo',
      'mov': 'video/quicktime',
      'wmv': 'video/x-ms-wmv',
      'flv': 'video/x-flv',
      'webm': 'video/webm',
      'mkv': 'video/x-matroska',
      '3gp': 'video/3gpp',
      'm4v': 'video/x-m4v'
    }
    
    return mimeTypes[ext] || 'video/mp4' // default to mp4
  }
  
  // Use the blob URL hook for automatic cleanup - centralized blob management
  const { blobUrl: videoUrl, isLoading, error } = useBlobUrlFromApi(
    // Fetch function with timeout
    shouldSkipVideoLoad ? null : (async () => {
      try {
        // Early return if we should skip video loading (double check)
        if (shouldSkipVideoLoad) {
          return null
        }

        if (video.objectId === null || video.objectId === undefined ||
            video.objectMovieId === null || video.objectMovieId === undefined ||
            !video.filenameExtension) {
          return null
        }

        // Additional heuristics for SatsueiDataKensaku to skip loading if we know the video doesn't exist
        if (pageType === 'satsueiDataKensaku') {
          // Skip if objectMovieId is 0 and no edit IDs
          if (video.objectMovieId === 0 && !video.objectMovieEditId && !video.maxObjectMovieEditId) {
            return null
          }

          // Skip if filenameExtension indicates "none", "null", or "empty"
          if (!video.filenameExtension || 
              video.filenameExtension.toLowerCase() === 'none' || 
              video.filenameExtension.toLowerCase() === 'null' || 
              video.filenameExtension.toLowerCase() === 'empty') {
            return null
          }
        }

        // Create timeout promise for faster "no video" detection
        const timeoutMs = pageType === 'satsueiDataKensaku' ? 3000 : 5000
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('Video load timeout')), timeoutMs)
        })

        // Fetch video content with timeout
        const fetchPromise = imageService.getMovie(
          video.objectId,
          video.objectMovieId,
          video.filenameExtension,
          ...serviceParams
        )

        const result = await Promise.race([fetchPromise, timeoutPromise])
        
        // Post-fetch validation for empty or very small blobs
        if (result && result.data) {
          if (result.data.size === 0 || result.data.size < 100) {
            return null
          }
        }
        
        // Return the blob data, not the full response
        return result.data
      } catch (error) {
        console.error('Error in VideoCard fetch function:', error)
        // Return null instead of throwing to show "no video" state
        return null
      }
    }),
    // Stable dependencies - only change when video data actually changes
    [videoKey, imageService, serviceParams, shouldSkipVideoLoad],
    // MIME type based on video extension
    getVideoMimeType(video.filenameExtension)
  )

  // Get video element reference for programmatic control
  const videoRef = useRef(null)

  // Video event handlers
  const handlePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause()
      } else {
        videoRef.current.play()
      }
    }
    setIsPlaying(!isPlaying)
  }

  const handleTimeUpdate = (e) => {
    setCurrentTime(e.target.currentTime)
  }

  const handleLoadedMetadata = (e) => {
    setDuration(e.target.duration)
  }

  const handleVideoClick = () => {
    if (onVideoClick) {
      onVideoClick(video)
    } else {
      handlePlayPause()
    }
  }

  const handleDownload = (e) => {
    e.stopPropagation()
    if (onDownload) {
      onDownload(video)
    } else if (videoUrl) {
      const link = document.createElement('a')
      link.href = videoUrl
      link.download = `${video.objectId}_${video.objectMovieId}.${video.filenameExtension}`
      link.click()
    }
  }

  const handleShare = (e) => {
    e.stopPropagation()
    if (onShare) {
      onShare(video)
    }
  }

  // Format time for display
  const formatTime = (time) => {
    const minutes = Math.floor(time / 60)
    const seconds = Math.floor(time % 60)
    return `${minutes}:${seconds.toString().padStart(2, '0')}`
  }

  // Show loading state
  if (isLoading) {
    return (
      <div className={`relative border border-gray-300 bg-gray-100 rounded-lg overflow-hidden w-[180px] h-[120px] md:w-[200px] md:h-[140px] lg:w-[220px] lg:h-[150px] flex items-center justify-center ${className}`} data-testid="video-card">
        <div className="text-center" data-testid="loading-state">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
          <p className="text-xs text-gray-500">読み込み中...</p>
        </div>
      </div>
    )
  }

  // Show error state
  if (error) {
    return (
      <div className={`relative border border-gray-300 bg-red-50 rounded-lg overflow-hidden w-[180px] h-[120px] md:w-[200px] md:h-[140px] lg:w-[220px] lg:h-[150px] flex items-center justify-center ${className}`} data-testid="video-card">
        <div className="text-center" data-testid="error-state">
          <Video className="h-8 w-8 text-red-400 mx-auto mb-2" />
          <p className="text-xs text-red-500">エラー</p>
        </div>
      </div>
    )
  }

  // Show uploading state (isUploaded=0: server has metadata but file not yet uploaded)
  if (shouldSkipVideoLoad) {
    return (
      <div className={`relative border border-gray-300 bg-gray-100 rounded-lg overflow-hidden w-[180px] h-[120px] md:w-[200px] md:h-[140px] lg:w-[220px] lg:h-[150px] flex items-center justify-center ${className}`} data-testid="video-card">
        <div className="text-center" data-testid="uploading-state">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-400 mx-auto mb-2"></div>
          <p className="text-xs text-gray-500">転送中...</p>
        </div>
      </div>
    )
  }

  // Show no video state
  if (!videoUrl) {
    return (
      <div className={`relative border border-gray-300 bg-gray-100 rounded-lg overflow-hidden w-[180px] h-[120px] md:w-[200px] md:h-[140px] lg:w-[220px] lg:h-[150px] flex items-center justify-center ${className}`} data-testid="video-card">
        <div className="text-center" data-testid="no-video-state">
          <Video className="h-8 w-8 text-gray-400 mx-auto mb-2" />
          <p className="text-xs text-gray-500">動画なし</p>
        </div>
      </div>
    )
  }

  // Show video player
  return (
    <div className={`relative border border-gray-300 bg-white rounded-lg overflow-hidden w-[180px] h-[120px] md:w-[200px] md:h-[140px] lg:w-[220px] lg:h-[150px] group ${className}`} data-testid="video-card">
      <video
        ref={videoRef}
        src={videoUrl}
        className="w-full h-full object-cover"
        controls={showControls}
        autoPlay={autoPlay}
        preload="metadata"
        playsInline
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onError={(e) => {
          console.error('Video error:', e)
          console.error('Video URL:', videoUrl)
          console.error('Video element:', e.target)
          console.error('Video error details:', {
            error: e.target.error,
            networkState: e.target.networkState,
            readyState: e.target.readyState,
            src: e.target.src
          })
        }}
        onClick={handleVideoClick}
        data-testid="video-element"
      />
      
      {/* Custom controls overlay */}
      {!showControls && (
        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center">
          <button
            onClick={handlePlayPause}
            className="bg-black bg-opacity-50 text-white rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
            data-testid="play-pause-button"
            aria-label={isPlaying ? "Pause video" : "Play video"}
          >
            {isPlaying ? <Pause className="h-6 w-6" /> : <Play className="h-6 w-6" />}
          </button>
        </div>
      )}

      {/* Action buttons */}
      <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
        {onDownload && (
          <button
            onClick={handleDownload}
            className="bg-black bg-opacity-50 text-white rounded-full p-1 hover:bg-opacity-70 transition-colors"
            data-testid="download-button"
            aria-label="Download video"
          >
            <Download className="h-4 w-4" />
          </button>
        )}
        {onShare && (
          <button
            onClick={handleShare}
            className="bg-black bg-opacity-50 text-white rounded-full p-1 hover:bg-opacity-70 transition-colors"
            data-testid="share-button"
            aria-label="Share video"
          >
            <Share2 className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Time display */}
      {duration > 0 && (
        <div className="absolute bottom-2 left-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded" data-testid="time-display" aria-live="polite">
          {formatTime(currentTime)} / {formatTime(duration)}
        </div>
      )}
    </div>
  )
}

export default VideoCard
